import type { ReactNode } from 'react';
import { Link } from 'react-router';
import clsx from 'clsx';
import { Info, Lightbulb } from '@/components/icons';

/** Building blocks of the guide's text, on the app's design tokens. */

export function P({ children }: { children: ReactNode }) {
  return <p className="leading-relaxed text-fg-secondary">{children}</p>;
}

export function H({ children }: { children: ReactNode }) {
  return <h3 className="pt-2 text-sm font-semibold text-fg">{children}</h3>;
}

/** Numbered how-to steps. */
export function Steps({ children }: { children: ReactNode }) {
  return <ol className="ml-5 list-decimal space-y-1.5 text-fg-secondary marker:font-semibold marker:text-primary">{children}</ol>;
}

export function Bullets({ children }: { children: ReactNode }) {
  return <ul className="ml-5 list-disc space-y-1.5 text-fg-secondary marker:text-fg-muted">{children}</ul>;
}

/** Bold UI words: a button, tab or field as it is labelled in the app. */
export function B({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-fg">{children}</strong>;
}

export function C({ children }: { children: ReactNode }) {
  return <code className="rounded-bar bg-surface-raised px-1.5 py-0.5 font-mono text-[0.85em] text-fg">{children}</code>;
}

export function Pre({ children }: { children: string }) {
  return <pre className="overflow-x-auto rounded-card bg-surface-sunken p-4 font-mono text-xs leading-relaxed text-fg ring-1 ring-border ring-inset">{children}</pre>;
}

/** A link to a page of the app ("Go to Test data"). */
export function To({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="font-medium text-link underline-offset-2 hover:underline">
      {children}
    </Link>
  );
}

/** A link to another section of the guide. */
export function See({ id, children }: { id: string; children: ReactNode }) {
  return (
    <a href={`#${id}`} className="font-medium text-link underline-offset-2 hover:underline">
      {children}
    </a>
  );
}

export function Tip({ children, title = 'Tip' }: { children: ReactNode; title?: string }) {
  return (
    <Callout icon={<Lightbulb className="size-4 text-primary" aria-hidden />} title={title} className="bg-primary-subtle ring-primary/25">
      {children}
    </Callout>
  );
}

export function Note({ children, title = 'Good to know' }: { children: ReactNode; title?: string }) {
  return (
    <Callout icon={<Info className="size-4 text-link" aria-hidden />} title={title} className="bg-surface-sunken ring-border">
      {children}
    </Callout>
  );
}

function Callout({ icon, title, className, children }: { icon: ReactNode; title: string; className: string; children: ReactNode }) {
  return (
    <div className={clsx('flex gap-3 rounded-card p-4 text-sm ring-1 ring-inset', className)}>
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="space-y-1">
        <div className="font-semibold text-fg">{title}</div>
        <div className="leading-relaxed text-fg-secondary">{children}</div>
      </div>
    </div>
  );
}

export function Table({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  return (
    <div className="overflow-x-auto rounded-card ring-1 ring-border ring-inset">
      <table className="w-full text-left text-sm">
        <thead className="bg-surface-sunken text-xs tracking-label text-fg-muted uppercase">
          <tr>
            {head.map((h) => (
              <th key={h} scope="col" className="px-4 py-2.5 font-semibold">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} className={clsx('px-4 py-2.5 align-top', j === 0 ? 'font-medium text-fg' : 'text-fg-secondary')}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
